/**
 * Admin — Rate Revisions review page.
 * Lists all rate revision requests with Pending / Approved / Rejected tabs.
 * Admin can Approve (updates BOQ item rate + emails info@rrl…) or Reject with an optional note.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Scales, ArrowUpRight, CheckCircle, XCircle, Clock, ArrowRight, ArrowDown, ArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const shortTs = (iso) => (iso || "").slice(0, 19).replace("T", " ");

const TAB_META = {
  pending: { label: "Pending", icon: Clock, color: "text-[#B06000]", bg: "bg-[#FEF7E0]" },
  approved: { label: "Approved", icon: CheckCircle, color: "text-[#137333]", bg: "bg-[#E6F4EA]" },
  rejected: { label: "Rejected", icon: XCircle, color: "text-[#C5221F]", bg: "bg-[#FCE8E6]" },
};

export default function AdminRateRevisions() {
  const { user } = useAuth();
  const { activeProjectId } = useProject();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [reviewDialog, setReviewDialog] = useState(null); // {req, action}

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: tab, page_size: 100 };
      if (activeProjectId) params.project_id = activeProjectId;
      const { data } = await api.get("/rate-revisions", { params });
      setRows(data.items || []);
      const s = await api.get("/rate-revisions/stats", { params: activeProjectId ? { project_id: activeProjectId } : {} });
      setStats(s.data || { pending: 0, approved: 0, rejected: 0 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, activeProjectId]);

  if (!isAdmin) {
    return <div className="p-8 text-center text-neutral-500 text-sm">Admin access required.</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto p-6 lg:p-8" data-testid="admin-rate-revisions-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
          <Scales size={30} className="text-primary" weight="fill" /> Rate Revision Requests
        </h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl leading-relaxed">
          Review proposed rate changes on BOQ items. Approving updates the item&apos;s rate immediately and notifies <span className="text-neutral-800 font-medium">info@rrlbuildersanddevelopers.com</span>. Rejection is also notified.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-4 flex gap-1" data-testid="rr-tabs">
        {["pending", "approved", "rejected"].map((k) => {
          const meta = TAB_META[k];
          const Icon = meta.icon;
          const active = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`rr-tab-${k}`}
              className={
                "px-4 py-2.5 -mb-[1px] border-b-2 flex items-center gap-2 text-sm font-medium transition-colors " +
                (active ? "border-primary text-primary" : "border-transparent text-neutral-500 hover:text-neutral-900")
              }
            >
              <Icon size={14} weight={active ? "fill" : "regular"} />
              {meta.label}
              <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-sm ${meta.bg} ${meta.color}`}>{stats[k] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid="rr-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
          <div className="col-span-3">Item · Description</div>
          <div className="col-span-2">Requested by</div>
          <div className="col-span-2 text-right">Current → Proposed</div>
          <div className="col-span-1 text-right">Δ</div>
          <div className="col-span-2">Reason</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-neutral-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center">
            <Scales size={40} className="mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500">No {tab} requests.</div>
          </div>
        ) : (
          rows.map((r) => {
            const delta = (r.proposed_rate || 0) - (r.current_rate || 0);
            const deltaPct = r.current_rate > 0 ? (delta / r.current_rate) * 100 : 0;
            const up = delta > 0;
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50/50 text-sm" data-testid={`rr-row-${r.id}`}>
                <div className="col-span-3 self-center min-w-0">
                  <Link to={`/boq/item/${r.boq_item_id}`} className="text-neutral-900 hover:text-primary hover:underline font-medium truncate block">{r.boq_item_name || "—"}</Link>
                  <div className="text-[11px] text-neutral-500 truncate">{r.description || "—"} · {r.unit || ""}</div>
                </div>
                <div className="col-span-2 self-center min-w-0">
                  <div className="text-neutral-900 truncate">{r.requested_by_name}</div>
                  <div className="text-[11px] text-neutral-500">{shortTs(r.requested_at)}</div>
                </div>
                <div className="col-span-2 self-center text-right tabular-nums text-neutral-900 flex items-center justify-end gap-1">
                  <span className="text-neutral-500">{fmtINR(r.current_rate)}</span>
                  <ArrowRight size={12} className="text-neutral-400" />
                  <span className="font-medium">{fmtINR(r.proposed_rate)}</span>
                </div>
                <div className={"col-span-1 self-center text-right tabular-nums flex items-center justify-end gap-0.5 " + (up ? "text-[#C5221F]" : "text-[#137333]")}>
                  {up ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                  {deltaPct.toFixed(1)}%
                </div>
                <div className="col-span-2 self-center text-[11px] text-neutral-600 leading-snug line-clamp-3">{r.reason}</div>
                <div className="col-span-2 self-center flex justify-end gap-1.5">
                  {r.status === "pending" ? (
                    <>
                      <Button size="sm" className="rounded-sm h-8 bg-[#137333] hover:bg-[#0d5624] text-white gap-1" onClick={() => setReviewDialog({ req: r, action: "approve" })} data-testid={`rr-approve-${r.id}`}>
                        <CheckCircle size={12} weight="fill" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 border-[#C5221F] text-[#C5221F] hover:bg-[#FCE8E6] gap-1" onClick={() => setReviewDialog({ req: r, action: "reject" })} data-testid={`rr-reject-${r.id}`}>
                        <XCircle size={12} weight="fill" /> Reject
                      </Button>
                    </>
                  ) : (
                    <div className="text-[11px] text-neutral-500 self-center text-right">
                      by <span className="text-neutral-800">{r.reviewed_by_name || "—"}</span><br />
                      <span className="text-neutral-400">{shortTs(r.reviewed_at)}</span>
                    </div>
                  )}
                </div>
                {r.status !== "pending" && r.review_note && (
                  <div className="col-span-12 mt-1 pt-2 border-t border-dashed border-neutral-200 text-[11px] text-neutral-500">
                    <span className="uppercase tracking-wider text-[9px] text-neutral-400 mr-2">Review note</span>
                    {r.review_note}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {reviewDialog && (
        <ReviewDialog
          req={reviewDialog.req}
          action={reviewDialog.action}
          onClose={() => setReviewDialog(null)}
          onDone={() => { setReviewDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function ReviewDialog({ req, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const isApprove = action === "approve";

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/rate-revisions/${req.id}/${action}`, { review_note: note });
      toast.success(isApprove ? "Approved · BOQ item rate updated" : "Rejected");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="rr-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isApprove ? "Approve" : "Reject"} rate revision</DialogTitle>
          <DialogDescription>
            {isApprove ? (
              <>Updates <span className="text-neutral-900 font-medium">{req.boq_item_name}</span>&apos;s rate from <span className="tabular-nums text-neutral-900">{fmtINR(req.current_rate)}</span> to <span className="tabular-nums text-[#B06000] font-medium">{fmtINR(req.proposed_rate)}</span>. Notifies info@rrlbuildersanddevelopers.com.</>
            ) : (
              <>Marks this request as rejected. No changes to the BOQ item. Requester will be notified.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-xs uppercase tracking-wider text-neutral-500">Review note (optional)</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context for the requester…" className="rounded-sm min-h-[80px]" data-testid="rr-review-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            className={isApprove ? "rounded-sm bg-[#137333] hover:bg-[#0d5624] text-white" : "rounded-sm bg-[#C5221F] hover:bg-[#a01816] text-white"}
            onClick={submit}
            disabled={saving}
            data-testid="rr-review-confirm"
          >
            {saving ? "Saving…" : (isApprove ? "Approve & Update" : "Reject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
